import { useEffect, useId } from "react";

import type { ServerFilters } from "@/hooks/useQueryFilters";
import { useTenant } from "@/providers/session";
import { gql, useQuery } from "@apollo/client";

export const useInvoiceLiveCountsSubscription = ({
  query = {},
}: {
  query?: ServerFilters;
}) => {
  const { tenantId } = useTenant();
  const id = useId();

  const {
    data: liveCounts,
    loading,
    error,
    subscribeToMore,
  } = useQuery(INVOICE_LIVE_COUNTS, {
    variables: {
      tenantId,
      query,
    },
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: false,
  });

  useEffect(() => {
    if (!tenantId) return;

    const unsubscribe = subscribeToMore({
      document: INVOICE_LIVE_COUNTS_SUBSCRIPTION,
      variables: {
        tenantId,
        sessionId: id,
        query,
      },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        const invoicesCounts = subscriptionData.data.invoiceLiveCounts;

        return {
          ...prev,
          invoicesCounts,
        };
      },
    });

    return unsubscribe;
  }, [tenantId, id, query, subscribeToMore]);

  const liveCountsNumber = Number.parseInt(liveCounts?.invoicesCounts);
  const isLoading = loading && isNaN(liveCountsNumber);

  return {
    liveCounts: liveCountsNumber,
    loading: isLoading,
    error,
  };
};

const INVOICE_LIVE_COUNTS_SUBSCRIPTION = gql`
  subscription InvoiceLiveCounts(
    $tenantId: String
    $sessionId: String
    $query: JSON
  ) {
    invoiceLiveCounts(tenantId: $tenantId, sessionId: $sessionId, query: $query)
  }
`;

const INVOICE_LIVE_COUNTS = gql`
  query InvoiceLiveCounts($tenantId: String, $query: JSON) {
    invoicesCounts(tenantId: $tenantId, query: $query)
  }
`;
